import { useQuery } from '@tanstack/react-query';

import { queryKeys } from '~/api/queryKeys';

import { getProductOutcomes } from '../services/delivery-runs.service';
import type { ProductOutcomeOption } from '../types/delivery-runs.types';

/**
 * Static fallback for the outcome menu while the endpoint is unreachable: the
 * two options every run supports. Mirrors the endpoint's own default (all) and
 * its `excludeOutcomes` form, so the tab stays usable and never sends an
 * outcome the server might 422.
 */
export const FALLBACK_PRODUCT_OUTCOMES: ProductOutcomeOption[] = [
  { id: 'ALL', label: 'All products', isDefault: true, query: {} },
  { id: 'NEEDS_ATTENTION', label: 'Needs attention', query: { excludeOutcomes: 'DELIVERED' } },
];

/**
 * The outcome filter menu, sourced from the API so the options match exactly
 * what the leaf endpoints accept. Static content, so it is cached for the
 * session; on failure the caller falls back to `FALLBACK_PRODUCT_OUTCOMES`.
 */
export function useProductOutcomes() {
  return useQuery({
    queryKey: queryKeys.deliveries.productOutcomes(),
    queryFn: getProductOutcomes,
    staleTime: Infinity,
    gcTime: 60 * 60_000,
    retry: 1,
  });
}

/** The option to select on first render: the one flagged default, else the first. */
export function defaultProductOutcome(
  options: ProductOutcomeOption[],
): ProductOutcomeOption | undefined {
  return options.find((o) => o.isDefault) ?? options[0];
}
